import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

/** Number of filled filter values, nested objects and arrays are counted per value. */
export const countActiveFilters = (value: unknown): number => {
  if (value == null) return 0;

  if (Array.isArray(value)) {
    return value.reduce<number>((count, item) => count + countActiveFilters(item), 0);
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? 0 : 1;
  }

  if (typeof value === 'string') {
    return value.trim().length ? 1 : 0;
  }

  if (typeof value === 'object') {
    return Object.values(value).reduce<number>(
      (count, item) => count + countActiveFilters(item),
      0
    );
  }

  return 0;
};

export const quickFilterChipClassName = (active: boolean) =>
  cn(
    'rounded-full border px-4 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
    active
      ? 'border-foreground bg-foreground text-background'
      : 'border-border bg-background text-foreground hover:bg-muted disabled:hover:bg-background'
  );

type QuickFilterChipProps = Omit<ComponentProps<'button'>, 'type'> & {
  active: boolean;
};

/** Pill toggle of the estates quick filters row, shared by the estates page and project estates. */
export const QuickFilterChip = ({ active, className, ...props }: QuickFilterChipProps) => (
  <button
    type="button"
    aria-pressed={active}
    className={cn(quickFilterChipClassName(active), className)}
    {...props}
  />
);
